import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, BookOpenText, ListChecks, GraduationCap, ChatCircleDots, Question, ClipboardText } from "@phosphor-icons/react";

const CARDS = [
  { to: "/app/frameworks", title: "Framework Explorer", desc: "NIST AI RMF, ISO 42001, EU AI Act", icon: BookOpenText },
  { to: "/app/assessment", title: "3rd-Party Assessment", desc: "The end-to-end 9-step process", icon: ListChecks },
  { to: "/app/interview", title: "Interview Q&A", desc: "Model answers · Easy → Hard", icon: Question },
  { to: "/app/quizzes", title: "Interactive Quizzes", desc: "Test yourself, track your best", icon: GraduationCap },
  { to: "/app/tutor", title: "AI Tutor", desc: "Ask Claude anything AI governance", icon: ChatCircleDots },
  { to: "/app/checklists", title: "Checklists", desc: "Copy-ready playbooks for day 1", icon: ClipboardText },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/progress/mine").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-10">
      <header className="fade-up">
        <div className="overline mb-3">Overview</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Welcome, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">
          Pick up where you left off. Everything you need to master AI risk management and third-party assessment lives here.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-border fade-up delay-1" data-testid="dashboard-metrics">
        <Metric label="Sections completed" value={progress?.sections_completed?.length ?? 0} />
        <Metric label="Quizzes attempted" value={progress?.total_attempts ?? 0} />
        <Metric label="Best quiz score"
          value={
            progress?.quiz_best?.length
              ? Math.max(...progress.quiz_best.map((q) => q.percent)) + "%"
              : "—"
          }
        />
      </section>

      <section className="fade-up delay-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-border">
          {CARDS.map((c) => (
            <Link key={c.to} to={c.to} className="group p-6 bg-white border-r border-b border-border hover:bg-secondary transition-colors flex flex-col justify-between min-h-[180px]"
              data-testid={`dashboard-card-${c.to.split("/").pop()}`}>
              <div>
                <c.icon size={24} weight="duotone" className="text-primary mb-3" />
                <div className="font-display text-2xl leading-tight mb-1">{c.title}</div>
                <div className="text-sm text-muted-foreground">{c.desc}</div>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm text-primary font-medium">
                Open <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-6 bg-white border-r border-b border-border">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-4xl mt-2 tabular-nums">{value}</div>
    </div>
  );
}
